import React, { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { RigidBody, CuboidCollider } from "@react-three/rapier";
import * as THREE from "three";

export const PILLAR_START_X = -14;
export const PILLAR_SPACING = 4;
export const PILLAR_COUNT = 8;

const pillarX = (i) => PILLAR_START_X + i * PILLAR_SPACING;

const Monolith = ({ index, activeRef }) => {
    const mat = useRef();
    const gem = useRef();
    const ringMat = useRef();
    const group = useRef();
    const glow = useRef(0);

    useFrame((state, delta) => {
        const isActive = activeRef.current === index;
        glow.current = THREE.MathUtils.lerp(glow.current, isActive ? 1 : 0, delta * 5);
        const g = glow.current;
        const t = state.clock.elapsedTime;
        if (mat.current) {
            mat.current.emissiveIntensity = 0.22 + g * 1.1;
            mat.current.emissive.lerpColors(
                new THREE.Color("#3b1d63"),
                new THREE.Color("#ec4899"),
                g
            );
            mat.current.color.lerpColors(
                new THREE.Color("#0d0817"),
                new THREE.Color("#2b1030"),
                g
            );
        }
        if (ringMat.current) {
            ringMat.current.emissiveIntensity = 0.6 + g * 2.5;
        }
        if (gem.current) {
            gem.current.rotation.y = t * (0.4 + g * 2.2);
            gem.current.position.y = 4.75 + Math.sin(t * 1.6 + index) * 0.12;
        }
        if (group.current) {
            const s = 1 + g * 0.05;
            group.current.scale.set(s, 1, s);
        }
    });

    return (
        <group ref={group} position={[pillarX(index), 0, 0]}>
            <mesh position={[0, 2.2, 0]}>
                <boxGeometry args={[1.15, 4.4, 1.15]} />
                <meshStandardMaterial
                    ref={mat}
                    color="#0d0817"
                    metalness={0.7}
                    roughness={0.3}
                    emissive="#3b1d63"
                    emissiveIntensity={0.25}
                />
            </mesh>
            {/* base ring */}
            <mesh position={[0, 0.06, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                <ringGeometry args={[1.0, 1.35, 40]} />
                <meshStandardMaterial
                    ref={ringMat}
                    color="#120a20"
                    emissive="#7dd3fc"
                    emissiveIntensity={0.6}
                    side={THREE.DoubleSide}
                />
            </mesh>
            {/* floating gem */}
            <mesh ref={gem} position={[0, 4.75, 0]}>
                <octahedronGeometry args={[0.28, 0]} />
                <meshStandardMaterial
                    color={index % 2 === 0 ? "#a855f7" : "#7dd3fc"}
                    emissive={index % 2 === 0 ? "#a855f7" : "#7dd3fc"}
                    emissiveIntensity={1.8}
                />
            </mesh>
        </group>
    );
};

const PillarMonoliths = ({ activeRef }) => {
    return (
        <group>
            {Array.from({ length: PILLAR_COUNT }, (_, i) => (
                <Monolith key={i} index={i} activeRef={activeRef} />
            ))}
            {/* fixed physics colliders so spheres bounce off pillars */}
            {Array.from({ length: PILLAR_COUNT }, (_, i) => (
                <RigidBody key={`col-${i}`} type="fixed" colliders={false}>
                    <CuboidCollider args={[0.6, 2.2, 0.6]} position={[pillarX(i), 2.2, 0]} />
                </RigidBody>
            ))}
        </group>
    );
};

export default PillarMonoliths;
